import { useState } from 'react'
import { Link } from '@tanstack/react-router'
import { useReminderActions, useReminders } from '../api/hooks'
import { Badge, EmptyState, ErrorState, Field, LoadingBlock, Section, formatDateTime } from '../components/ui'

const TABS = [
  { key: 'due', label: 'Fällig' },
  { key: 'upcoming', label: 'Kommend' },
  { key: 'done', label: 'Erledigt' },
] as const

function inDays(days: number): string {
  const date = new Date()
  date.setDate(date.getDate() + days)
  return date.toISOString()
}

export function RemindersPage() {
  const [tab, setTab] = useState<(typeof TABS)[number]['key']>('due')
  const query = useReminders(tab)
  const actions = useReminderActions()
  const [draft, setDraft] = useState({ title: '', due_at: '', body: '' })

  return (
    <>
      <h1 className="mb-4 text-2xl">Erinnerungen</h1>

      <div role="tablist" aria-label="Filter" className="mb-4 flex gap-1">
        {TABS.map((entry) => (
          <button
            key={entry.key}
            role="tab"
            type="button"
            aria-selected={tab === entry.key}
            className={`btn ${tab === entry.key ? 'btn-secondary' : 'btn-ghost'}`}
            onClick={() => setTab(entry.key)}
          >
            {entry.label}
          </button>
        ))}
      </div>

      {query.isPending ? (
        <LoadingBlock />
      ) : query.isError ? (
        <ErrorState error={query.error} onRetry={() => void query.refetch()} />
      ) : query.data.length === 0 ? (
        <EmptyState
          title="Nichts hier"
          description={tab === 'due' ? 'Keine fälligen Erinnerungen.' : 'Diese Liste ist leer.'}
        />
      ) : (
        <ul className="space-y-2">
          {query.data.map((reminder) => (
            <li key={reminder.id} className="card">
              <div className="flex flex-wrap items-start gap-2">
                <div className="min-w-0 flex-1">
                  <p className="font-medium">{reminder.title}</p>
                  {reminder.body ? <p className="mt-0.5 text-sm text-paper-500">{reminder.body}</p> : null}
                  <p className="mt-1 text-xs text-paper-500">
                    {formatDateTime(reminder.due_at)} · {reminder.kind === 'auto' ? 'automatisch' : 'manuell'}
                    {reminder.source_rule ? ` (${reminder.source_rule})` : ''}
                  </p>
                </div>
                {reminder.close_suggested_by ? (
                  <Badge tone="ok">Abschluss vorgeschlagen</Badge>
                ) : null}
              </div>

              <div className="mt-3 flex flex-wrap gap-2">
                {reminder.status !== 'done' ? (
                  <>
                    <button
                      type="button"
                      className="btn-secondary"
                      onClick={() => actions.complete.mutate(reminder.id)}
                    >
                      Erledigt
                    </button>
                    <button
                      type="button"
                      className="btn-ghost"
                      onClick={() => actions.snooze.mutate({ id: reminder.id, until: inDays(3) })}
                    >
                      3 Tage verschieben
                    </button>
                  </>
                ) : null}
                {reminder.document_id ? (
                  <Link
                    to="/dokumente/$documentId"
                    params={{ documentId: reminder.document_id }}
                    className="btn-ghost"
                  >
                    Dokument
                  </Link>
                ) : null}
                {reminder.case_id ? (
                  <Link to="/vorgaenge/$caseId" params={{ caseId: reminder.case_id }} className="btn-ghost">
                    Vorgang
                  </Link>
                ) : null}
              </div>
            </li>
          ))}
        </ul>
      )}

      <Section title="Eigene Erinnerung">
        <form
          className="card"
          onSubmit={(event) => {
            event.preventDefault()
            if (!draft.title || !draft.due_at) return
            actions.create.mutate(
              { title: draft.title, due_at: new Date(draft.due_at).toISOString(), body: draft.body },
              { onSuccess: () => setDraft({ title: '', due_at: '', body: '' }) },
            )
          }}
        >
          <Field label="Titel">
            {(props) => (
              <input
                {...props}
                className="input"
                required
                value={draft.title}
                onChange={(event) => setDraft({ ...draft, title: event.target.value })}
              />
            )}
          </Field>
          <Field label="Fällig am">
            {(props) => (
              <input
                {...props}
                className="input"
                type="datetime-local"
                required
                value={draft.due_at}
                onChange={(event) => setDraft({ ...draft, due_at: event.target.value })}
              />
            )}
          </Field>
          <Field label="Notiz">
            {(props) => (
              <textarea
                {...props}
                className="input"
                rows={2}
                value={draft.body}
                onChange={(event) => setDraft({ ...draft, body: event.target.value })}
              />
            )}
          </Field>
          <button type="submit" className="btn-primary" disabled={actions.create.isPending}>
            Anlegen
          </button>
        </form>
      </Section>
    </>
  )
}
